import {
  ScheduledEventDto,
  ScheduledEventFormType,
  SuggestedScheduledEventDto,
  UserDto,
} from '@act/shared/data-transfer-objects';
import { DateTime } from 'luxon';
import { RefObject, useEffect, useMemo, useState } from 'react';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';
import { NoteFieldRef } from '../NoteField';
import {
  buildScheduledEventAddressOptions,
  buildScheduledEventTimeOptions,
  resolveScheduledEventTimeMillisForOptions,
  resolveVisitLocationOnEventTypeChange,
} from '../scheduledEventDialog.util';
import {
  buildScheduledEventFormConfig,
  normalizeScheduledEventNoteForDefault,
} from '../scheduledEventForm.util';
import { resolveSuggestedScheduledEventNoteDraftId } from '../scheduledEventNoteDraft.util';
import { FormConfig } from 'stories/forms/FormBuilder/formBuilder.types';
import { useScheduledEventNoteSaveOverride } from './useScheduledEventNoteSaveOverride.hook';

export type UseScheduledEventFormConfigProps = {
  control: Control<ScheduledEventFormType>;
  setValue: UseFormSetValue<ScheduledEventFormType>;
  user: UserDto;
  scheduledEvent?: ScheduledEventDto;
  suggestedEvent?: SuggestedScheduledEventDto;
  isEditMode: boolean;
  noteFieldRef: RefObject<NoteFieldRef | null>;
  includeTrackingNames?: boolean;
};

export type UseScheduledEventFormConfigResult = {
  formConfig: FormConfig<ScheduledEventFormType>;
  showRecoveredNote: boolean;
};

/**
 * Reactive FormBuilder config and visit-field side effects for scheduled-event forms.
 * Subscribes to event type and visit location via useWatch so useScheduledEventForm
 * does not need standing watch() subscriptions for dynamic field layout.
 * @param props - Form control, member context, and note-field wiring.
 * @returns FormBuilder config and recovered-note header state.
 */
export function useScheduledEventFormConfig({
  control,
  setValue,
  user,
  scheduledEvent,
  suggestedEvent,
  isEditMode,
  noteFieldRef,
  includeTrackingNames = false,
}: UseScheduledEventFormConfigProps): UseScheduledEventFormConfigResult {
  const [eventType, visitLocationId] = useWatch({
    control,
    name: ['eventType', 'visitLocationId'],
  });

  const [showRecoveredNote, setShowRecoveredNote] = useState(false);

  const defaultsSource = useMemo(
    () => ({ scheduledEvent, suggestedEvent }),
    [scheduledEvent, suggestedEvent]
  );

  const { scheduledEventNoteSaveOverride } = useScheduledEventNoteSaveOverride({
    userId: user.id,
    scheduledEvent,
  });

  const eventNoteDraftId = useMemo(
    () =>
      suggestedEvent
        ? resolveSuggestedScheduledEventNoteDraftId(suggestedEvent)
        : undefined,
    [suggestedEvent?.id, suggestedEvent?.callId, suggestedEvent?.type]
  );

  const suggestedNoteBaseline = useMemo(
    () => normalizeScheduledEventNoteForDefault(suggestedEvent?.note),
    [suggestedEvent?.note]
  );

  const addressOptions = useMemo(
    () => buildScheduledEventAddressOptions(user?.addresses),
    [user?.addresses]
  );

  const timeOptions = useMemo(
    () =>
      buildScheduledEventTimeOptions(
        resolveScheduledEventTimeMillisForOptions(defaultsSource)
      ),
    [defaultsSource]
  );

  useEffect(() => {
    if (visitLocationId !== -1) {
      setValue('otherLocation', null);
    }
  }, [visitLocationId, setValue]);

  useEffect(() => {
    const updates = resolveVisitLocationOnEventTypeChange({
      eventType,
      visitLocationId,
      addressOptions,
    });

    if (updates) {
      if (updates.visitLocationId !== undefined) {
        setValue('visitLocationId', updates.visitLocationId);
      }
      if (updates.otherLocation !== undefined) {
        setValue('otherLocation', updates.otherLocation);
      }
    }
  }, [eventType, visitLocationId, addressOptions, setValue]);

  const noteMode = isEditMode ? 'edit' : 'create';

  const formConfig = useMemo(
    () =>
      buildScheduledEventFormConfig({
        eventType,
        visitLocationId,
        addressOptions,
        timeOptions,
        minDate: DateTime.now(),
        noteFieldRef,
        noteMode,
        userId: user.id,
        scheduledEventId: scheduledEvent?.id,
        noteId: scheduledEvent?.noteId,
        draftId: eventNoteDraftId,
        draftDiscardBaseline: suggestedEvent ? suggestedNoteBaseline : undefined,
        noteSave: isEditMode ? scheduledEventNoteSaveOverride : undefined,
        includeTrackingNames,
        onRecoveredNoteChange: setShowRecoveredNote,
      }),
    [
      eventType,
      visitLocationId,
      addressOptions,
      timeOptions,
      noteMode,
      user.id,
      scheduledEvent?.id,
      scheduledEvent?.noteId,
      eventNoteDraftId,
      suggestedNoteBaseline,
      suggestedEvent,
      scheduledEventNoteSaveOverride,
      includeTrackingNames,
      noteFieldRef,
    ]
  );

  return { formConfig, showRecoveredNote };
}
